import { z } from 'zod';
import { aiGuidedPresentationSchema } from './guided-content.js';
/** Product key format — allowed products are enforced by backend registry/wrappers only. */
export const aiProductKeySchema = z
    .string()
    .regex(/^[a-z][a-z0-9-]{1,63}$/, 'Invalid productKey format');
export const aiTenantKeySchema = z.string().min(1).max(128);
/** Config scope key: product-default or tenant-specific tenantKey. */
export const AI_PRODUCT_DEFAULT_SCOPE_KEY = '__product_default__';
export const aiScopeSchema = z.object({
    productKey: aiProductKeySchema,
    tenantKey: aiTenantKeySchema,
});
export const aiSessionStatusSchema = z.enum(['active', 'completed', 'expired', 'handoff']);
export const aiFsmStepSchema = z.string().min(1).max(64);
export const aiCatalogServiceSchema = z.object({
    serviceId: z.string().min(1),
    name: z.string().min(1),
    category: z.string().nullable(),
    durationMin: z.number().int().positive().nullable(),
    priceFrom: z.number().nonnegative().nullable(),
    isActive: z.boolean(),
    /**
     * Описание из прайса. Подбор ищет и по нему: клиент говорит «сухость», а в
     * названии услуги этого слова нет ни разу — оно есть в описании.
     */
    description: z.string().nullable().optional(),
    /**
     * Разметка ограничений позиции.
     *
     * Отсутствие поля означает «не размечено», а не «ограничений нет»: позиция
     * без разметки клиенту с названными особенностями не предлагается.
     */
    constraints: z
        .object({
        blockedBy: z.array(z.string()),
        reviewed: z.boolean(),
        unmapped: z.array(z.string()).optional(),
    })
        .optional(),
});
export const aiStaffSchema = z.object({
    staffId: z.string().min(1),
    displayName: z.string().min(1),
    serviceIds: z.array(z.string()).default([]),
});
export const aiSlotSchema = z.object({
    staffId: z.string().min(1),
    serviceId: z.string().min(1),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    time: z.string().regex(/^\d{2}:\d{2}$/),
    seanceLengthSec: z.number().int().positive().nullable().optional(),
});
export const aiSessionAdvanceInputSchema = z.object({
    action: z.string().min(1).max(64),
    payload: z.record(z.unknown()).optional(),
});
export const aiPublicSessionCapabilitiesSchema = z
    .object({
    nativeBooking: z.boolean(),
})
    .strict();
export const aiPublicSessionViewSchema = z.object({
    sessionId: z.string().uuid(),
    status: aiSessionStatusSchema,
    fsmSnapshot: z.record(z.unknown()),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    recommendation: z
        .object({
        serviceIds: z.array(z.string()),
        route: z.string(),
    })
        .optional(),
    presentation: z.lazy(() => aiGuidedPresentationSchema).nullable().optional(),
    contentError: z.enum(['ai_content_unavailable']).optional(),
    capabilities: aiPublicSessionCapabilitiesSchema.optional(),
});
/** Internal session view (includes scope metadata for backend). */
export const aiSessionViewSchema = aiPublicSessionViewSchema.extend({
    productKey: aiProductKeySchema,
    tenantKey: aiTenantKeySchema,
});
export const aiRecommendationResultSchema = z.object({
    serviceIds: z.array(z.string()).max(3),
    confidence: z.enum(['high', 'low', 'none']),
    route: z.enum(['recommendation', 'consultation', 'handoff']),
});
export const aiSafetyDecisionSchema = z.object({
    flags: z.array(z.string()),
    nextStep: aiFsmStepSchema.optional(),
    requiresHandoff: z.boolean().default(false),
});
/** Keys that must never appear in persisted FSM snapshots or event params. */
export const AI_FORBIDDEN_STORAGE_KEYS = [
    'transcript',
    'phone',
    'email',
    'fullname',
    'name',
    'message',
    'freeText',
    'free_text',
    'medicalText',
    'clientMessage',
];
export function assertSafeStorageObject(value, path = 'root') {
    if (value == null || typeof value !== 'object')
        return;
    if (Array.isArray(value)) {
        for (const [index, entry] of value.entries()) {
            assertSafeStorageObject(entry, `${path}[${index}]`);
        }
        return;
    }
    for (const [key, nested] of Object.entries(value)) {
        if (AI_FORBIDDEN_STORAGE_KEYS.includes(key)) {
            throw new Error(`Forbidden storage key at ${path}.${key}`);
        }
        assertSafeStorageObject(nested, `${path}.${key}`);
    }
}
//# sourceMappingURL=contracts.js.map